import { createSelector } from '@reduxjs/toolkit';
import { equals, isEmpty, isNil, keys, pathOr, pick, values } from 'ramda';

import ooeConstants from '../constants';
import { submitWarningErrorMessages } from '../util/customerErrorMessages';
import { capitalizeFirstLetter, formatPrice } from '../util/format';
import { isObjWithEmptyStrings } from '../util/utils';
import { getVariation } from '../services/launchDarkly';
import type { DeliveryError, State } from './reducers';
import { validatedToOrderDeliveryAddress } from './transforms';

// Order selectors
export const selectOrder = (state: { order: State }): State => state.order;
export const selectOrderForDXE = createSelector(selectOrder, (order) => {
  const orderPropsForDXE = [
    'orderType',
    'status',
    'destination',
    'companyName',
    'id',
    'promiseDateTime',
    'paperGoods',
    'externalOrderInfo',
    'totalAmount',
  ] as const;
  const cleanedUpOrder: Pick<State, (typeof orderPropsForDXE)[number]> = pick(orderPropsForDXE, order);
  const deliveryAddress = {
    ...validatedToOrderDeliveryAddress.parse(order.validatedDeliveryAddress),
    ...(order.deliveryAddressPlaceId ? { placeId: order.deliveryAddressPlaceId } : {}),
  };

  if (!isNil(order.editOrderId)) {
    return {
      ...cleanedUpOrder,
      id: order.editOrderId,
      deliveryAddress,
    };
  }

  return { ...cleanedUpOrder, deliveryAddress };
});

export const selectTransactionId = createSelector(selectOrder, (order) => order.transactionId);

// Destination and Delivery selectors
export const selectLastAddressSentForValidation = createSelector(
  selectOrder,
  (order) => order.lastAddressSentForValidation,
);
export const selectDestination = createSelector(selectOrder, (order) => order.destination);
export const selectDeliveryAddress = createSelector(selectOrder, (order) => ({
  streetNumber: order.validatedDeliveryAddress.streetNumber ?? '',
  route: order.validatedDeliveryAddress.route ?? '',
  subpremise: order.validatedDeliveryAddress.subpremise ?? '',
  locality: order.validatedDeliveryAddress.locality ?? '',
  administrativeAreaLevelOne: order.validatedDeliveryAddress.administrativeAreaLevelOne ?? '',
  postalCode: order.validatedDeliveryAddress.postalCode ?? '',
  companyName: order.companyName,
}));
export const selectDeliveryAddressPlaceId = createSelector(
  selectOrder,
  (order) => order.deliveryAddressPlaceId,
);
export const selectDeliveryAddressForOrder = createSelector(selectOrder, (order) => ({
  ...order.deliveryAddress,
  companyName: order.companyName,
}));
export const selectEditOrderSavedDeliveryAddress = createSelector(selectOrder, (order) => ({
  ...order.editOrderSavedDeliveryAddress,
  companyName: order.companyName,
}));
export const selectDeliveryAddressValid = createSelector(selectDeliveryAddress, (address) =>
  Boolean(
    address.streetNumber &&
      address.route &&
      address.locality &&
      address.administrativeAreaLevelOne &&
      address.postalCode,
  ),
);
export const selectDeliveryAddressChanged = createSelector(
  selectDeliveryAddressForOrder,
  selectEditOrderSavedDeliveryAddress,
  (currentAddress, savedAddress) => !equals(currentAddress, savedAddress),
);
export const selectDestinationValid = createSelector(
  selectDeliveryAddressValid,
  selectDestination,
  (validated, destination) => {
    let valid;
    if (destination === ooeConstants.PICKUP) {
      valid = true;
    }
    if (destination === ooeConstants.DELIVERY) {
      valid = validated;
    }
    return valid;
  },
);
export const selectShowWarningModal = createSelector(selectOrder, (order) => order.showWarningModal);

export const selectAvailableLocations = createSelector(selectOrder, (order) => order.availableLocations);

export const selectApartmentSuite = createSelector(
  selectOrder,
  (order) => order.validatedDeliveryAddress?.subpremise,
);

// Tax and Total selectors
export const selectTaxAmount = createSelector(selectOrder, (order) => order.taxAmount ?? 0);

export const selectSubTotalAmount = createSelector(selectOrder, (order) => order.subTotalAmount ?? 0);

export const selectTotalAmount = createSelector(selectOrder, (order) => order.totalAmount ?? 0);

export const selectFormattedSubTotalAmount = createSelector(selectOrder, (order) =>
  formatPrice(order.subTotalAmount),
);

export const selectFormattedTaxAmount = createSelector(selectTaxAmount, (taxAmount) =>
  formatPrice(taxAmount),
);

// Date and Time selectors
export const selectAvailableDates = createSelector(selectOrder, (order) => order.availableDates);

// Loading selectors
export const selectOrderIsLoading = createSelector(selectOrder, (order) => order.loading.updateOrder);
export const selectSubmitOrderLoading = createSelector(selectOrder, (order) => order.loading.submitOrder);
export const selectValidateAddressLoading = createSelector(
  selectOrder,
  (order) => order.loading.validateAddress,
);
export const selectValidateDeliveryRangeLoading = createSelector(
  selectOrder,
  (order) => order.loading.validateDeliveryRange,
);

export const selectDatesLoading = createSelector(selectOrder, (order) => order.loading.availableDates);

// Error selectors
export const selectOrderError = createSelector(selectOrder, (order) => order.error);
export const selectDeliveryError = createSelector(
  selectOrderError,
  (error): DeliveryError => error.validateAddress || error.validateDeliveryRange,
);

export const selectValidAddressError = createSelector(selectOrderError, (error) => error.validateAddress);
export const selectDeliveryRangeWarning = createSelector(
  selectOrderError,
  (error) => error.validateDeliveryRange,
);
export const selectUpdateOrderError = createSelector(selectOrderError, (error) => error.updateOrder);
export const selectOrderErrors = createSelector(selectOrderError, (error) =>
  values(pick(['updateOrder'], error)),
);
export const selectOrderUpdateErrors = createSelector(selectOrderError, (error) =>
  values(pick(['updateOrder'], error)),
);
export const selectHasOrderUpdateErrors = createSelector(
  selectOrderErrors,
  (orderErrors) => orderErrors.filter((error) => Boolean(error)).length > 0,
);
export const selectDeliveryAddressSubmitWarning = createSelector(
  selectDestination,
  selectDeliveryAddressValid,
  selectValidAddressError,
  selectDeliveryRangeWarning,
  (destination, allFieldsFilled, deliveryError, deliveryWarning) => {
    if (destination === ooeConstants.DELIVERY && !allFieldsFilled) {
      return {
        type: 'error',
        message: submitWarningErrorMessages.MISSING_FIELDS,
      };
    }
    if (destination === ooeConstants.DELIVERY && !isNil(deliveryError)) {
      return {
        type: 'error',
        message: submitWarningErrorMessages.ADDRESS_WARNING,
      };
    }
    if (destination === ooeConstants.DELIVERY && !isNil(deliveryWarning)) {
      return {
        type: 'warning',
        message: submitWarningErrorMessages.DELIVERY_RANGE,
      };
    }
    return {};
  },
);

export const selectDeliveryTip = createSelector(selectOrder, (order) => order.deliveryTip);

export const selectDeliveryTipAmount = createSelector(
  selectDeliveryTip,
  (deliveryTip) => deliveryTip?.tipAmount ?? 0,
);

export const selectDeliveryTipPercentOfSale = createSelector(
  selectDeliveryTip,
  (deliveryTip) => deliveryTip?.tipPercentOfSale ?? 0,
);

// Edit Order selectors
export const selectEditOrderId = createSelector(selectOrder, (order) => order.editOrderId);
export const selectEditMode = createSelector(selectOrder, (order) => !isNil(order.editOrderId));
export const selectReorder = createSelector(selectOrder, (order) => order.reorder);
export const selectEditOrderOriginalCustomerDetails = createSelector(
  selectOrder,
  (order) => order.editOrderOriginalCustomerDetails,
);
export const selectEditOrderOriginalCustomerCFAId = createSelector(
  selectEditOrderOriginalCustomerDetails,
  (details) => details?.cfaId ?? '',
);
export const selectHideNewPaymentOption = createSelector(selectEditMode, selectOrder, (editMode, order) => {
  const { editOrderStatus } = order;
  return editMode && editOrderStatus === 'Submit';
});
export const selectOverrideGuestDetails = createSelector(
  selectOrder,
  (order) => order.editOverrideGuestDetails,
);
export const selectOverrideGuestDetailsModalOpen = createSelector(
  selectOrder,
  (order) => order.editOverrideGuestDetailsModalOpen,
);

//Paper Goods Selectors
export const selectPaperGoodsOptions = createSelector(selectOrder, (order) => order.paperGoodsOptions);

export const selectIsEditCMTOrder = createSelector(selectOrder, (order) => {
  const client = pathOr(null, ['editOrderClient'], order);
  return client === 'ooe_masquerade' || client === 'MyPayments';
});

export const selectIsEditWebOrder = createSelector(selectOrder, (order) => {
  const client = pathOr<string | null>(null, ['editOrderClient'], order);
  return !!client && ooeConstants.WEB_CLIENT_IDS.some((id) => id === client);
});

// "Paper Goods must be chosen": new order, OR editing a Web (PWO) order.
const selectShowItemizedPaperGoods = () => getVariation('showPaperGoods', false);

const selectPaperGoodsMustBeChosen = createSelector(
  selectEditMode,
  selectIsEditWebOrder,
  selectShowItemizedPaperGoods,
  (isEditMode, isWebOrder, showItemizedPaperGoods) => !showItemizedPaperGoods && (!isEditMode || isWebOrder),
);

export const selectPaperGoodsError = createSelector(
  selectPaperGoodsOptions,
  selectPaperGoodsMustBeChosen,
  (options, mustChoose) => (mustChoose && isEmpty(options) ? ['Paper Goods'] : []),
);

export const selectPaperGoodsRequired = createSelector(
  selectPaperGoodsOptions,
  selectPaperGoodsMustBeChosen,
  (options, mustChoose) => mustChoose && isEmpty(options),
);

export const selectShowDefaultPaperGoodsMessage = createSelector(
  selectEditMode,
  selectIsEditCMTOrder,
  selectIsEditWebOrder,
  selectPaperGoodsOptions,
  (isEdit, isCMT, isWeb, paperGoodsOptions) => !!(isEdit && !isCMT && !isWeb && isEmpty(paperGoodsOptions)),
);

export const selectPaperGoodsBool = createSelector(selectOrder, (order) => order.paperGoods);

export const selectPaperGoodsYesOrNo = createSelector(selectPaperGoodsOptions, (options) => {
  if (!isEmpty(options) && Object.values(options).some((opt) => opt === true)) {
    return 'yes';
  }
  if (!isEmpty(options) && Object.values(options).every((opt) => opt === false)) {
    return 'no';
  }
  return undefined;
});

export const selectSubmitOrderErrorModalOpen = createSelector(
  selectOrder,
  (order) => order.submitOrderErrorModalOpen,
);

export const selectSubmitOrderErrorMessage = createSelector(
  selectOrder,
  (order) => order.submitOrderErrorMessage,
);

export const selectSmsConsent = createSelector(selectOrder, (order) => order.smsConsent);

// Secondary contact selectors
export const selectSecondaryContactErrors = createSelector(
  selectOrder,
  (order) => order.secondaryContactErrors,
);
export const selectSecondaryContactValues = createSelector(selectOrder, (order) => ({
  firstName: order.secondaryContactFirstName,
  lastName: order.secondaryContactLastName,
  phoneNumber: order.secondaryContactPhoneNumber,
}));
export const selectSecondaryContactValid = createSelector(selectSecondaryContactErrors, (errors) =>
  isEmpty(errors),
);
export const selectSecondaryContactSubmitWarning = createSelector(
  selectSecondaryContactValues,
  selectSecondaryContactErrors,
  (scValues, scErrors) => {
    if (isObjWithEmptyStrings(scValues) || isEmpty(scErrors)) {
      return {};
    }
    const fieldLabels = keys(scErrors).map((field) => {
      if (field === 'firstName' || field === 'lastName') {
        return capitalizeFirstLetter(`${field.slice(0, -4)} name`);
      }
      if (field === 'phoneNumber') {
        return capitalizeFirstLetter(`${field.slice(0, -6)} number`);
      }
      return field;
    });
    const { length } = fieldLabels;
    const formatted = fieldLabels.map((name, idx) =>
      length > 1 && idx === length - 1 ? `and ${name}` : name,
    );
    const preposition = length === 1 ? 'is a' : 'are';
    const plural = length === 1 ? '' : 's';
    return {
      type: 'error',
      message: `Secondary POC ${formatted.join(' ')} ${preposition} required field${plural}.`,
    };
  },
);

// Details form selectors
export const selectDetailsDate = createSelector(selectOrder, (order) => order.detailsDate);
export const selectDetailsTime = createSelector(selectOrder, (order) => order.detailsTime);
export const selectDetailsGuestCount = createSelector(selectOrder, (order) => order.detailsGuestCount);
export const selectDetailsSpecialInstructions = createSelector(
  selectOrder,
  (order) => order.detailsSpecialInstructions,
);
export const selectDetailsCateringReason = createSelector(
  selectOrder,
  (order) => order.detailsCateringReason,
);
export const selectDetailsErrors = createSelector(selectOrder, (order) => order.detailsErrors);

export const selectDetailsFormValues = createSelector(
  selectDetailsDate,
  selectDetailsTime,
  selectDetailsGuestCount,
  selectDetailsSpecialInstructions,
  selectDetailsCateringReason,
  selectPaperGoodsBool,
  (date, time, guestCount, specialInstructions, cateringReason, paperGoods) => ({
    date,
    time,
    guestCount,
    specialInstructions,
    cateringReason,
    paperGoods,
  }),
);

export const selectDetailsFormSyncErrors = createSelector(selectDetailsErrors, (errors) => {
  if (!errors) {
    return ['date', 'time'];
  }
  const errorKeys = Object.keys(errors).filter((key) => errors[key as keyof typeof errors]);
  return errorKeys.length > 0 ? errorKeys : [];
});

// Payment method selectors
export const selectSelectedPaymentMethod = createSelector(
  selectOrder,
  (order) => order.selectedPaymentMethod,
);

export const selectNewPaymentMethod = createSelector(
  selectSelectedPaymentMethod,
  (method) => method === ooeConstants.REQUEST_PAYMENT,
);
